import React, { useRef, useEffect } from "react";
import { formatDistanceToNow } from "date-fns";
import { RotateCcw, User, Sparkles, Info } from "lucide-react";
import { Bot } from "lucide-react";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: Date;
}

interface ChatHistoryProps {
  messages: ChatMessage[];
}

export function ChatHistory({ messages }: ChatHistoryProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  if (messages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <div className="w-16 h-16 rounded-full bg-[#1f6feb]/20 flex items-center justify-center mb-4">
          <Sparkles className="h-8 w-8 text-[#58a6ff]" />
        </div>
        <h3 className="text-white font-medium mb-2">Start Creating</h3>
        <p className="text-gray-400 text-sm max-w-xs">
          Choose a template or describe what you'd like to create
        </p>
        <div className="mt-6 space-y-2 w-full">
          <div className="bg-[#161b22] hover:bg-[#1f6feb]/20 p-3 rounded-lg cursor-pointer transition-colors">
            <p className="text-sm text-white">
              Create a hero section with a gradient background and two columns
            </p>
          </div>
          <div className="bg-[#161b22] hover:bg-[#1f6feb]/20 p-3 rounded-lg cursor-pointer transition-colors">
            <p className="text-sm text-white">
              Generate a pricing table with three tiers and a highlighted plan
            </p>
          </div>
          <div className="bg-[#161b22] hover:bg-[#1f6feb]/20 p-3 rounded-lg cursor-pointer transition-colors">
            <p className="text-sm text-white">
              Make a testimonial grid with customer quotes and images
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {messages.map((message) => (
        <div key={message.id} className="message-container">
          {message.role === "user" && (
            <div className="flex justify-end mb-4">
              <div className="max-w-[85%]">
                <div className="bg-[#1f6feb] text-white px-4 py-3 rounded-2xl rounded-tr-sm">
                  <p className="text-sm">{message.content}</p>
                </div>
                <div className="text-right">
                  <span className="text-xs text-gray-500 mt-1 inline-block">
                    {formatDistanceToNow(message.timestamp, {
                      addSuffix: true,
                    })}
                  </span>
                </div>
              </div>
            </div>
          )}

          {message.role === "assistant" && (
            <div className="flex mb-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#238636]/20 flex items-center justify-center mr-2">
                <Sparkles className="h-4 w-4 text-[#3fb950]" />
              </div>
              <div className="max-w-[85%]">
                <div className="bg-[#161b22] text-white px-4 py-3 rounded-2xl rounded-tl-sm">
                  <p className="text-sm">{message.content}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-500 mt-1 inline-block">
                    {formatDistanceToNow(message.timestamp, {
                      addSuffix: true,
                    })}
                  </span>
                </div>
              </div>
            </div>
          )}

          {message.role === "system" && (
            <div className="flex items-center justify-center my-6">
              <div className="bg-[#161b22]/50 text-gray-400 text-xs py-2 px-4 rounded-full flex items-center">
                <Info className="h-3 w-3 mr-2" />
                <span>{message.content}</span>
              </div>
            </div>
          )}
        </div>
      ))}
      <div ref={messagesEndRef} />
    </div>
  );
}
